import Vue from "vue";
import _ from "lodash";
import API from "@/api/index";
import FvuMonitorStore from "@/store/modules/fvu_monitor";

const MODULE_NAME = "fvu";

const MUTATION_TYPES = {
	ADD_FVU: "ADD_FVU",
	CLEAR_FVUS: "CLEAR_FVUS",
	CLEAR_MESSAGE_QUEUE: "CLEAR_MESSAGE_QUEUE",
	DELETE_FVU: "DELETE_FVU",
	QUEUE_MESSAGE: "QUEUE_MESSAGE",
	UPDATE_POSITION: "UPDATE_POSITION"
};

const ACTION_TYPES = {
	INIT_FVUS: "INIT_FVUS",
	INGEST_MESSAGES: "INGEST_MESSAGES"
};

export default {
	MODULE_NAME,
	MUTATION_TYPES,
	ACTION_TYPES,
	namespaced: true,
	state: {
		fvus: {},
		messageQueues: {}
	},
	mutations: {
		[MUTATION_TYPES.ADD_FVU](state, payload) {
			if (_.isEmpty(payload)) return;
			const putFVU = fvu => {
				return fvu.fvuId
					? Vue.set(state.fvus, fvu.fvuId, fvu)
					: console.error(new Error("ADD_FVU: Invalid fvu object."));
			};
			if (Array.isArray(payload)) {
				payload.map(putFVU);
			} else {
				putFVU(payload);
			}
		},
		[MUTATION_TYPES.CLEAR_FVUS](state) {
			Vue.set(state, "fvus", {});
		},
		[MUTATION_TYPES.CLEAR_MESSAGE_QUEUE](state, payload) {
			const fvuId = getFvuId(payload);
			if (!fvuId) {
				throw new Error(
					"store.fvu.QUEUE_MESSAGE: Invalid message; cannot find fvu id."
				);
			}
			state.messageQueues[fvuId] = (state.messageQueues[fvuId] || []).slice(
				0,
				0
			);
		},
		[MUTATION_TYPES.DELETE_FVU](state, payload) {
			Vue.delete(state.fvus, payload);
		},
		[MUTATION_TYPES.QUEUE_MESSAGE](state, payload) {
			const fvuId = getFvuId(payload);
			if (!fvuId) {
				throw new Error(
					"store.fvu.QUEUE_MESSAGE: Invalid message; cannot find fvu id."
				);
			}
			state.messageQueues[fvuId] = (state.messageQueues[fvuId] || []).concat([
				payload
			]);
		},
		[MUTATION_TYPES.UPDATE_POSITION](state, payload) {
			const fvuId = payload.fvuId;
			const fvu = state.fvus[fvuId];
			const forceUpdate = !!payload.forceUpdate;
			if (forceUpdate) delete payload.forceUpdate;
			try {
				if (!_.isNumber(payload.timestamp)) {
					payload.timestamp = new Date(payload.timestamp).getTime();
				}
			} catch (e) {
				console.warn(e);
			}
			var rejection;
			if (payload.timestamp < fvu.timestamp) {
				rejection = new Error(
					`${MODULE_NAME}/${MUTATION_TYPES.UPDATE_POSITION}: Rejecting change for ${fvuId} because incoming timestamp is older than existing timestamp: ${payload.timestamp} < ${fvu.timestamp}.`
				);
			} else if (
				!_.isEmpty(fvu.gpsData) &&
				payload.gpsData.speed <
					FvuMonitorStore.MODULE_CONFIG.MIN_NON_STATIONARY_SPEED
			) {
				rejection = new Error(
					`${MODULE_NAME}/${MUTATION_TYPES.UPDATE_POSITION}: Rejecting change for ${fvuId} because incoming speed is less than minimum non-stationary speed: ${payload.gpsData.speed} < ${FvuMonitorStore.MODULE_CONFIG.MIN_NON_STATIONARY_SPEED}.`
				);
			}
			if (rejection && !forceUpdate) {
				console.debug(rejection);
				return false;
			}
			state.fvus[fvuId].timestamp = payload.timestamp;
			state.fvus[fvuId].gpsData = { ...payload.gpsData };
		}
	},
	actions: {
		[ACTION_TYPES.INIT_FVUS](context) {
			return API.getWithParamsJson(
				API.ENDPOINTS.GET_VUNITS_WITH_LATEST_LOCATION,
				{},
				{},
				e => {
					throw e;
				}
			)
				.catch(e => {
					console.warn(e);
					return [];
				})
				.then(payload => {
					_.each(payload, row => {
						row.fvuId = getFvuId(row);
						// if (/0006$/.test(row.fvuId)) return;
						row.gpsData = row.gpsData || {};
						row.timestamp = row.timestamp || new Date().getTime();
						context.commit(MUTATION_TYPES.ADD_FVU, row);
					});
				});
		},
		[ACTION_TYPES.INGEST_MESSAGES](context, payload) {
			return new Promise((resolve, reject) => {
				const fvuId = getFvuId(payload);
				if (!fvuId) {
					reject(
						new Error(
							"store.fvu.INGEST_MESSAGES: Invalid message; cannot find fvu id."
						)
					);
				}
				const fvuMessageQueue = context.state.messageQueues[fvuId];
				if (!fvuMessageQueue) {
					throw new Error(
						"store.fvu.INGEST_MESSAGES: No messageQueue found for fvuId '" +
							fvuId +
							"."
					);
				}
				const latestMessage = _.reduceRight(fvuMessageQueue, (ret, message) => {
					if (message.timestamp > ret.timestamp) {
						console.debug(
							"store.fvu.INGEST_MESSAGES: Out of order messages found for fvuId '" +
								fvuId +
								"': " +
								ret.timestamp +
								" " +
								message.timestamp
						);
						return message;
					}
					return ret;
				});
				if (fvuMessageQueue.length) {
					context.commit(MUTATION_TYPES.CLEAR_MESSAGE_QUEUE, payload);
				}
				if (!latestMessage) {
					// There is a messageQueue for this fvuId, but there's nothing in it, so this must be a case where we've fallen behind and discarded something.
					resolve(false);
				} else {
					const fvu = {
						fvuId: fvuId,
						device: latestMessage.device,
						serial: latestMessage.serial,
						gpsData: latestMessage.gpsData,
						timestamp: latestMessage.timestamp
					};
					if (!context.state.fvus[fvuId]) {
						resolve(context.commit(MUTATION_TYPES.ADD_FVU, fvu));
					} else {
						resolve(context.commit(MUTATION_TYPES.UPDATE_POSITION, fvu));
					}
				}
			});
		}
	},
	getters: {
		allFvus(state) {
			return state.fvus;
		},
		fvuId() {
			return getFvuId;
		},
		fvuPosition() {
			return fvu => {
				if (fvu === null || fvu === void 0) return null;
				if (
					!("gpsData" in fvu) ||
					!("latitude" in fvu.gpsData) ||
					!_.isNumber(fvu.gpsData.latitude) ||
					!("longitude" in fvu.gpsData) ||
					!_.isNumber(fvu.gpsData.longitude)
				)
					return null;
				return [fvu.gpsData.latitude, fvu.gpsData.longitude];
			};
		},
		fvuHeading() {
			return fvu => {
				if (!fvu) return null;
				if (!("gpsData" in fvu) || !("heading" in fvu.gpsData)) return null;
				return fvu.gpsData.heading;
			};
		},
		byIdentifier(state) {
			return fvuId => {
				return state.fvus[fvuId] ? { ...state.fvus[fvuId] } : null;
			};
		}
	}
};

function getFvuId(fvu) {
	return (
		fvu.fvuId ||
		[fvu.device, fvu.serial]
			.filter(k => !!k)
			.map(s => s.trim())
			.join("/")
	);
}
